/**
 * Future<T> represents a `value: T` that will be available in the future,
 * unless it isn't.
 *
 * Futures are built on Promises, mutating themselves when an underlying
 * promise resolves to make the Promise's data available synchronously.
 * 
 * a Future<T> goes through the following states:
 *   - `Loading<T> = { loading: Promise<T> }`: the future is still loading
 *   - `Ok<T> = { data: T }`: the future is present with `data`
 *   - `Failed = { error: any }`: the future has failed with `error`

 *
 * a Future's `.sync()` method synchronously returns the future as `Ok<T>`.
 * It throws if the value is not yet available or has failed. If you happen to
 * be in a React component render, this will suspend the component until
 * the Future is resolved.
 *
 * you can:
 *   - create Futures in any state with the `future: Loading<T>`,
 *     `present(): Ok<T>`, and `failed: Failed<T>` functions
 *   - await them (`Future<T>` is `Thenable<T>`).
 *   - convert to a promise with `.promise`
 */
import { createObject } from '../object';

export type Future<T> = {
  readonly promise: Promise<T>;
  readonly status: 'loading' | 'ok' | 'failed';

  /** You can await futures */
  then: Promise<T>['then'];

  /** Return the successful future or throw. */
  sync(): Future<T> & Ok<T>;

  /** Return the value or throw */
  unwrap(): T;
} & (Loading<T> | Ok<T> | Failed);

export type Loading<T> = {
  readonly loading: Promise<T>;
  readonly data?: never;
  readonly error?: never;
};

export type Ok<T> = {
  /**
   * The resolved value of the future, or undefined if the future is still loading
   * or already rejected.
   */
  readonly data: T;
  readonly loading?: never;
  readonly error?: never;
};

export type Failed = {
  readonly error: any;
  readonly loading?: never;
  readonly data?: never;
};

const BASE_FUTURE = {
  get promise() {
    if (this.loading) return this.loading;
    if (this.error) return Promise.reject(this.error);
    return Promise.resolve(this.data);
  },

  get status() {
    if (this.loading) return 'loading';
    if (this.error) return 'failed';
    return 'ok';
  },

  then: function futureThen(onfulfilled, onrejected) {
    return this.promise.then(onfulfilled, onrejected);
  } as PromiseLike<any>['then'],

  unwrap(): any {
    return this.sync().data;
  },

  /**
   * Return this future if it's Ok, throws otherwise.
   *
   * @throws a promise if the future is still loading, or an error if the future failed.
   * @param future
   * @returns
   */
  sync(): Future<any> & Ok<any> {
    if (this.loading) {
      throw this.loading;
    }
    if (this.error) {
      throw this.error;
    }
    return this as Future<any> & Ok<any>;
  },
};

function createFuture<T, U extends Partial<Future<T>>>(
  state: U
): Future<T> & U {
  return createObject(BASE_FUTURE as Partial<U>, state) as Future<T> & U;
}

export function future<T>(promise: Promise<T>): Future<T> & Loading<T> {
  const f = createFuture<T, Loading<T>>({
    loading: promise.then(
      (value) => {
        (f as any).data = value;
        delete (f as any).loading;
        return value;
      },
      (error) => {
        (f as any).error = error;
        delete (f as any).loading;
        throw error;
      }
    ),
  });
  return f;
}

export function present<T>(data: T): Future<T> & Ok<T> {
  return createFuture<T, Ok<T>>({ data });
}

export function failed(error: any): Future<never> & Failed {
  return createFuture<never, Failed>({ error });
}
